/**
 * «Отделения» карточки хозяйства (AK-746, wireframe экран 4).
 *
 * Карточки отделений с составом. Перенос полей отсюда НЕ делается — он живёт
 * во вкладке «Поля» (отметил чекбоксами → «В отделение»): перекладывать сорок
 * полей мышью невозможно, а два места для одного действия расходятся.
 *
 * Группа «Без отделения» — постоянная и легитимная (AK-739), а не «остаток,
 * который надо разобрать»: отделение при заведении поля необязательно, иначе
 * завод поля превращается в шлагбаум.
 */
import { Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Stack } from '@/components/ui/stack';
import { formatHa } from './OverviewTab';
import type { FarmBrigadeRow, FarmSummary } from './types';

export function BrigadesTab({
  summary,
  canWrite,
  onCreate,
  onEdit,
  onOpenFields,
}: {
  summary: FarmSummary;
  canWrite: boolean;
  onCreate: () => void;
  onEdit: (brigade: FarmBrigadeRow) => void;
  onOpenFields: (brigadeId: string | null) => void;
}) {
  return (
    <Stack gap={4}>
      <div className="flex items-center gap-3">
        <p className="flex-1 text-sm text-muted-foreground">
          {summary.brigades_count === 0
            ? 'Отделений пока нет — хозяйство работает одним куском, и это нормально.'
            : `${summary.brigades_count} отделения · ${summary.fields_without_brigade} полей вне отделений`}
        </p>
        {canWrite && (
          <Button size="sm" onClick={onCreate} data-testid="brigade-create">
            <Plus className="mr-2 h-4 w-4" />
            Отделение
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {summary.brigades.map((brigade) => (
          <Card key={brigade.id ?? 'none'}>
            <CardHeader className="p-4 pb-3">
              <CardTitle className="text-sm">{brigade.name}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {brigade.id === null
                  ? 'Легитимная группа, а не ошибка'
                  : brigade.leader_name
                    ? `${brigade.leader_name}${brigade.leader_phone ? ` · ${brigade.leader_phone}` : ''}`
                    : 'Руководитель не назначен'}
              </p>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Stack gap={3}>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <Fact label="Полей" value={String(brigade.fields_count)} />
                  <Fact label="Пашня" value={formatHa(brigade.arable_ha)} />
                  <Fact label="В плане" value={String(brigade.planned_fields)} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenFields(brigade.id)}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Поля отделения
                  </Button>
                  {canWrite && brigade.id !== null && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(brigade)}>
                      Изменить
                    </Button>
                  )}
                </div>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </div>
    </Stack>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="block text-xs uppercase text-muted-foreground">{label}</span>
      <span className="block font-semibold tabular-nums">{value}</span>
    </div>
  );
}
